import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  makeStyles,
} from '@material-ui/core';
import { alpha } from '@material-ui/core/styles';
import { fetchApiRef, useApi } from '@backstage/core-plugin-api';
import {
  TechDocsAddonLocations,
  createTechDocsAddonExtension,
  techdocsStorageApiRef,
  useTechDocsReaderPage,
} from '@backstage/plugin-techdocs-react';
import { techdocsPlugin } from '@backstage/plugin-techdocs';

import {
  VersionsManifest,
  computeVersionSwitch,
  currentVersionFromPath,
  selectableVersions,
} from './versioning';
import { fetchVersionsManifest } from './versionsCache';
import { useTierNavigation } from './useTierNavigation';

const useStyles = makeStyles(theme => ({
  formControl: {
    minWidth: 120,
    marginLeft: theme.spacing(2),
  },
  label: {
    color: theme.page.fontColor,
    '&.Mui-focused': {
      color: theme.page.fontColor,
    },
  },
  select: {
    color: theme.page.fontColor,
    '&:before': {
      borderBottomColor: alpha(theme.page.fontColor, 0.5),
    },
    '&:hover:not(.Mui-disabled):before': {
      borderBottomColor: theme.page.fontColor,
    },
    '&:after': {
      borderBottomColor: theme.page.fontColor,
    },
    '& .MuiSelect-icon': {
      color: theme.page.fontColor,
    },
  },
}));

/**
 * Header-location TechDocs addon (Story 9.3) that renders a Material-UI v4
 * version dropdown built from the entity's `versions.json`. Reads the current
 * entity from `useTechDocsReaderPage()` and the current version from the URL,
 * and navigates on change preserving the page sub-path. Renders nothing when
 * the entity has no manifest or only `latest` (AC 5) — failing closed.
 *
 * Story 6.3 (AC 6): tier-aware. Reads the *current tier's* manifest —
 * `<entity>/<tier>/versions.json` when the URL carries a validated tier
 * segment, the entity-root `versions.json` otherwise — so version switches
 * stay within the current tier. Un-tiered entities behave exactly as before.
 */
function VersionPickerAddon(): JSX.Element | null {
  const classes = useStyles();
  const { entityRef } = useTechDocsReaderPage();
  const { pathname, search, hash } = useLocation();
  const navigate = useNavigate();
  const storageApi = useApi(techdocsStorageApiRef);
  const fetchApi = useApi(fetchApiRef);
  const tierNavigation = useTierNavigation();

  const [manifest, setManifest] = useState<VersionsManifest | null>(null);

  const entityKey =
    `${entityRef.kind}:${entityRef.namespace}:${entityRef.name}`.toLowerCase();

  const tierLoading = tierNavigation.loading;
  const urlTier = tierNavigation.urlTier;

  useEffect(() => {
    let active = true;
    // Reset to the fail-closed state before the next entity's fetch resolves so
    // the picker never renders the previous entity's versions (or navigates with
    // a stale manifest). Mirrors TechDocsReaderRedirect's reset-on-entity-change.
    setManifest(null);
    if (tierLoading) {
      // Wait for the tier config before choosing a manifest scope — fetching
      // the root manifest for a tiered entity would hide the picker with a
      // stale null while the tier manifest is what the URL calls for.
      return undefined;
    }
    fetchVersionsManifest(
      storageApi,
      fetchApi,
      entityRef,
      urlTier ?? undefined,
    ).then(result => {
      if (active) {
        setManifest(result);
      }
    });
    return () => {
      active = false;
    };
    // entityKey + tier scope capture the identities that change the fetch; storageApi is stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityKey, tierLoading, urlTier, storageApi]);

  const options = useMemo(
    () => (manifest ? selectableVersions(manifest) : []),
    [manifest],
  );

  // AC 5: no picker for unversioned entities (no manifest, or only `latest`).
  if (!manifest || options.length < 2) {
    return null;
  }

  const detected = currentVersionFromPath(pathname, manifest.versions);
  const current = options.includes(detected) ? detected : options[0];

  const handleChange = async (chosen: string) => {
    if (chosen === current) {
      return;
    }
    const switchTo = computeVersionSwitch(
      pathname,
      manifest.versions,
      chosen,
      tierNavigation.tiers.map(t => t.id),
      entityRef,
    );
    let destination = switchTo.path;
    if (switchTo.hasSubPath) {
      try {
        // AC 4: only preserve the sub-path if it exists in the target version;
        // otherwise fall back to that version's root index. getEntityDocs
        // appends `/index.html` itself, so pass the directory path (which ends
        // in `/`) — appending index.html here would double it and always 404.
        await storageApi.getEntityDocs(entityRef, switchTo.docPath);
      } catch {
        destination = switchTo.rootPath;
      }
    }
    // Preserve any query string / anchor from the current URL so switching
    // versions doesn't silently drop reader query params or a #section anchor.
    navigate(`${destination}${search}${hash}`);
  };

  return (
    <FormControl className={classes.formControl} size="small">
      <InputLabel className={classes.label} id="techdocs-version-picker-label">
        Version
      </InputLabel>
      <Select
        className={classes.select}
        labelId="techdocs-version-picker-label"
        value={current}
        onChange={event => handleChange(String(event.target.value))}
      >
        {options.map(version => (
          <MenuItem key={version} value={version}>
            {version}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

export const VersionPicker = techdocsPlugin.provide(
  createTechDocsAddonExtension({
    name: 'VersionPicker',
    location: TechDocsAddonLocations.Header,
    component: VersionPickerAddon,
  }),
);
